import { isDefined, isNotDefined } from '@sgme/fp';
import { Spacer, VStack } from '@sgme/ui';
import deepEqual from 'fast-deep-equal';
import { memo, useCallback } from 'react';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderClientWay } from '@/App/tiles/orders/components/OrderClientWay.tsx';
import { OrderFooter } from '@/App/tiles/orders/components/OrderFooter.tsx';
import { OrderHeader } from '@/App/tiles/orders/components/OrderHeader.tsx';
import { OrderNotifications } from '@/App/tiles/orders/components/OrderNotifications.tsx';
import { OrderNotional } from '@/App/tiles/orders/components/OrderNotional.tsx';
import { ConnectedTakeProfitOrderSpot } from '@/App/tiles/orders/TakeProfitOrder/ConnectedTakeProfitOrderSpot.tsx';
import { TakeProfitCommercialMargin } from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/TakeProfitCommercialMargin.tsx';
import {
  useTakeProfitIsChecking,
  useTakeProfitIsModifyingField,
  useTakeProfitSetIsModifyingField,
  useTakeProfitTileId,
  useValidateTakeProfit,
} from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext.tsx';
import { TakeProfitCustomerPrice } from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/TakeProfitCustomerPrice.tsx';
import { TakeProfitExpiry } from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/TakeProfitExpiry.tsx';
import { TakeProfitLimitPrice } from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/TakeProfitLimitPrice.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { UsertypeGuard } from '@/components/UsertypeGuard.tsx';
import { getConfig } from '@/config/config.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { useSubmitTakeProfitMutation } from '@/store/orders/takeProfitOrder/api/index.ts';
import {
  selectNotificationEmailsTakeProfit,
  selectTakeProfitClientWayData,
  selectTakeProfitErrors,
  selectTakeProfitNotionalData,
  selectTakeProfitTile,
} from '@/store/orders/takeProfitOrder/selectors.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { selectOrderEmails } from '@/store/userPreferences/selectors.ts';

export const TakeProfitOrderCreationTile = memo(() => {
  const track = useTrackEvent();
  const dispatch = useAppDispatch();
  const tileId = useTakeProfitTileId();
  const setIsModifyingField = useTakeProfitSetIsModifyingField();
  const isModifyingField = useTakeProfitIsModifyingField();

  const notionalData = useAppSelector((state) => selectTakeProfitNotionalData(state, tileId));
  const clientWayData = useAppSelector((state) => selectTakeProfitClientWayData(state, tileId));
  const notificationEmails = useAppSelector((state) => selectNotificationEmailsTakeProfit(state, tileId));
  const preferencesNotificationEmails = useAppSelector(selectOrderEmails);

  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const errors = useAppSelector((state) => selectTakeProfitErrors(state, tileId));
  const validate = useValidateTakeProfit();
  const [submitTakeProfit] = useSubmitTakeProfitMutation({
    selectFromResult: () => ({}),
  });

  const isChecking = useTakeProfitIsChecking();

  const { areFieldsVisible, tile } = useAppSelector((state) => {
    const tile = selectTakeProfitTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        areFieldsVisible: false,
      };
    }

    return {
      tile,
      areFieldsVisible: isDefined(tile.properties.currencyPair),
    };
  }, deepEqual);

  const handleSubmit = useCallback(() => {
    track('Order action', 'Submit Order');
    if (isNotDefined(tile)) {
      console.error(`[handleSubmit] Tile with id ${tileId} not found in state.`);
      return;
    }

    if (isNotDefined(clientId)) {
      console.error('[handleSubmit] Client id is not defined.');
      return;
    }

    const notificationEmails = tile.properties.notificationEmails ?? preferencesNotificationEmails;

    if (isNotDefined(notificationEmails)) {
      console.error('[handleSubmit] Notification emails not found in the tile or user preferences.');
      return;
    }

    if (isNotDefined(tile.properties.currencyPair)) {
      console.error('[handleSubmit] Currency pair is not defined in the tile.');
      return;
    }

    submitTakeProfit({
      tileId,
      currency1: tile.properties.currencyPair.currency1,
      currency2: tile.properties.currencyPair.currency2,
      clientId,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      limitPrice: tile.properties.limitPrice,
      customerPrice: tile.properties.customerPrice,
      margin: tile.properties.commercialMargin,
      expiry: tile.properties.expiry,
      notificationEmails: tile.properties.notificationEmails ?? [],
      localTimeWithOffSet: tile.properties.clockOffset !== undefined ? Date.now() + tile.properties.clockOffset : 0,
    })
      .unwrap()
      .catch(({ errors }: { type: 'failure'; errors: ValidationResponseDetailContent[] }) => {
        console.error('Failed to submit take profit order:', errors);
        if (errors.some((error) => error.errorDescription === 'timeout')) {
          const params: SendHelpRequestRequest = {
            config: 'orderpending',
            details: JSON.stringify({
              payload: errors,
              orderIdToUpdate: tileId,
            }),
            data: {
              orderDescription: 'creation',
              orderType: 'limit',
              /**
               * send 'g10' or 'emerging' on order creation when we are able to differentiate
               */
              orderCurrencyType: 'unknown',
              orderId: tileId,
            },
            templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
            templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
          };

          logger.logError('Order submission failed on timeout for {tileId_s}', tileId);
          dispatch(sendHelpRequestThunk(params));
        }
      });
  }, [tileId, clientId, preferencesNotificationEmails, tile, submitTakeProfit]);

  return (
    <AutoAdvanceForm className="d-flex w-100 h-100">
      <VStack className="bg-lvl1 py-2" fullWidth fullHeight>
        <OrderHeader orderType="TakeProfitOrder" tile={tile} tileId={tileId} validate={validate} />

        {areFieldsVisible && (
          <>
            <div
              className="mx-16px"
              style={{
                display: 'grid',
                gap: '5px',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                gridTemplateRows: '20px',
                alignItems: 'center',
              }}
            >
              <ConnectedTakeProfitOrderSpot />
              <OrderClientWay clientWayData={clientWayData} validate={validate} />
              <OrderNotional
                errors={errors}
                notionalData={notionalData}
                validate={validate}
                setIsModifyingField={setIsModifyingField}
              />
              <UsertypeGuard allow="Internal Sales">
                <TakeProfitLimitPrice />
              </UsertypeGuard>
              <TakeProfitCustomerPrice />
              <UsertypeGuard allow="Internal Sales">
                <TakeProfitCommercialMargin />
              </UsertypeGuard>
              <TakeProfitExpiry />

              <OrderNotifications
                tileId={tileId}
                errors={errors}
                notificationEmails={notificationEmails}
                validate={validate}
              />
            </div>
            <Spacer />

            <OrderFooter tile={tile} isModifyingField={isModifyingField} handleSubmit={handleSubmit} />
          </>
        )}
      </VStack>
      {isChecking && (
        <div className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75">
          <div className="spinner blinker-lg" />
        </div>
      )}
    </AutoAdvanceForm>
  );
});
